/**
 * CommandPalette -- floating dropdown for / slash commands
 * Positioned at the caret using anchorRect coordinates
 */

import { useRef, useEffect } from 'react'
import {
  FilePlus,
  CheckCircle,
  HelpCircle,
  GitCompare,
  Tag,
  ShieldCheck,
  Users,
  Search,
} from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import type { SlashCommand, SlashCommandCategory } from '@/types/rich-input'

const ICON_MAP: Record<string, React.ElementType> = {
  FilePlus,
  CheckCircle,
  HelpCircle,
  GitCompare,
  Tag,
  ShieldCheck,
  Users,
  Search,
}

const CATEGORY_LABELS: Record<SlashCommandCategory, string> = {
  policy: 'Policy',
  attributes: 'Attributes',
  compliance: 'Compliance',
  data: 'Data',
  subjects: 'Subjects',
}

const CATEGORY_ORDER: readonly SlashCommandCategory[] = [
  'policy',
  'attributes',
  'compliance',
  'subjects',
  'data',
]

interface CommandPaletteProps {
  readonly commands: readonly SlashCommand[]
  readonly selectedIndex: number
  readonly anchorRect: { top: number; left: number } | null
  readonly onSelect: (command: SlashCommand) => void
}

export function CommandPalette({
  commands,
  selectedIndex,
  anchorRect,
  onSelect,
}: CommandPaletteProps) {
  const listRef = useRef<HTMLDivElement>(null)

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selected = listRef.current.querySelector(
        '[data-selected="true"]'
      ) as HTMLElement | null
      selected?.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  if (!anchorRect || commands.length === 0) return null

  // Group commands by category
  const grouped = new Map<SlashCommandCategory, SlashCommand[]>()
  for (const cmd of commands) {
    const existing = grouped.get(cmd.category) ?? []
    grouped.set(cmd.category, [...existing, cmd])
  }

  let flatIndex = 0

  return (
    <div
      className="absolute z-50 w-72 rounded-lg border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 duration-150"
      style={{
        top: anchorRect.top,
        left: anchorRect.left,
      }}
      role="listbox"
      aria-label="Slash commands"
    >
      <ScrollArea className="max-h-80">
        <div ref={listRef} className="p-1">
          {CATEGORY_ORDER.filter((cat) => grouped.has(cat)).map((category) => {
            const cmds = grouped.get(category) ?? []
            return (
              <div key={category}>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  {CATEGORY_LABELS[category]}
                </div>
                {cmds.map((cmd) => {
                  const currentIndex = flatIndex
                  flatIndex += 1
                  const isSelected = currentIndex === selectedIndex
                  const Icon = ICON_MAP[cmd.icon] ?? HelpCircle

                  return (
                    <button
                      key={cmd.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-selected={isSelected}
                      id={`command-option-${currentIndex}`}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors',
                        isSelected
                          ? 'bg-accent text-accent-foreground'
                          : 'hover:bg-accent/50'
                      )}
                      onClick={() => onSelect(cmd)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex-1 min-w-0 text-left">
                        <span className="font-medium">/{cmd.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground truncate">
                          {cmd.description}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </ScrollArea>
    </div>
  )
}
